/**
 * Team Manager - Admin panel for staff management
 *
 * Add, edit, terminate members and manage access tiers
 */

import { useState } from 'react'
import {
  Plus,
  Search,
  MoreVertical,
  Shield,
  UserX,
  Key,
  ChevronDown,
} from 'lucide-react'
import { cn } from '../../lib/utils'

type AccessTier = 'admin' | 'manager' | 'member' | 'guest'
type MemberStatus = 'active' | 'pending' | 'suspended' | 'terminated'

interface TeamMember {
  id: string
  name: string
  email: string
  role: string
  accessTier: AccessTier
  status: MemberStatus
  avatar: string
  joinedDate: string
}

const ACCESS_TIERS: { value: AccessTier; label: string; color: string }[] = [
  { value: 'admin', label: 'Admin', color: 'text-red-400 bg-red-500/10' },
  { value: 'manager', label: 'Manager', color: 'text-purple-400 bg-purple-500/10' },
  { value: 'member', label: 'Member', color: 'text-blue-400 bg-blue-500/10' },
  { value: 'guest', label: 'Guest', color: 'text-gray-400 bg-gray-500/10' },
]

const MOCK_MEMBERS: TeamMember[] = [
  { id: '1', name: 'Alex Chen', email: 'alex@lukhas.ai', role: 'Lead Engineer', accessTier: 'admin', status: 'active', avatar: 'AC', joinedDate: '2024-01-01' },
  { id: '2', name: 'Maya Patel', email: 'maya@lukhas.ai', role: 'Product Manager', accessTier: 'manager', status: 'active', avatar: 'MP', joinedDate: '2024-02-15' },
  { id: '3', name: 'Carlos Ruiz', email: 'carlos@lukhas.ai', role: 'Backend Dev', accessTier: 'member', status: 'active', avatar: 'CR', joinedDate: '2024-03-10' },
  { id: '4', name: 'Yuki Tanaka', email: 'yuki@lukhas.ai', role: 'Designer', accessTier: 'member', status: 'pending', avatar: 'YT', joinedDate: '2024-06-01' },
  { id: '5', name: 'Hans Mueller', email: 'hans@lukhas.ai', role: 'Consultant', accessTier: 'guest', status: 'active', avatar: 'HM', joinedDate: '2024-05-20' },
]

export default function TeamManager() {
  const [members, setMembers] = useState(MOCK_MEMBERS)
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState<string | null>(null)

  const filtered = members.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase())
  )

  const updateTier = (memberId: string, tier: AccessTier) => {
    setMembers(prev => prev.map(m =>
      m.id === memberId ? { ...m, accessTier: tier } : m
    ))
    setSelectedMember(null)
  }

  const updateStatus = (memberId: string, status: MemberStatus) => {
    setMembers(prev => prev.map(m =>
      m.id === memberId ? { ...m, status } : m
    ))
    setSelectedMember(null)
  }

  const activeCount = members.filter(m => m.status === 'active').length
  const pendingCount = members.filter(m => m.status === 'pending').length

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">Team</h1>
          <p className="text-gray-500 mt-1">{activeCount} active · {pendingCount} pending</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-lambda-gold text-black rounded-lg hover:bg-lambda-gold/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Add member</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none focus:border-white/20 transition-colors"
        />
      </div>

      {/* Member List */}
      <div className="space-y-2">
        {filtered.map(member => {
          const tierInfo = ACCESS_TIERS.find(t => t.value === member.accessTier)!
          const isSelected = selectedMember === member.id

          return (
            <div
              key={member.id}
              className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors"
            >
              {/* Avatar */}
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium',
                member.status === 'active' ? 'bg-lambda-gold/20 text-lambda-gold' :
                member.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-500/20 text-gray-400'
              )}>
                {member.avatar}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{member.name}</span>
                  {member.status === 'pending' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                      Pending
                    </span>
                  )}
                  {member.status === 'suspended' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">
                      Suspended
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 truncate">{member.email}</p>
              </div>

              {/* Role */}
              <span className="text-sm text-gray-400 hidden sm:block">{member.role}</span>

              {/* Access Tier */}
              <span className={cn(
                'text-xs px-2 py-1 rounded-lg',
                tierInfo.color
              )}>
                {tierInfo.label}
              </span>

              {/* Actions */}
              <div className="relative">
                <button
                  onClick={() => setSelectedMember(isSelected ? null : member.id)}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {/* Dropdown */}
                {isSelected && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-[#1a1a1f] border border-white/10 rounded-xl shadow-xl z-10 overflow-hidden">
                    <div className="p-2 border-b border-white/5">
                      <p className="text-xs text-gray-500 px-2 py-1">Change access tier</p>
                      {ACCESS_TIERS.map(tier => (
                        <button
                          key={tier.value}
                          onClick={() => updateTier(member.id, tier.value)}
                          className={cn(
                            'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left transition-colors',
                            member.accessTier === tier.value ? 'bg-white/10' : 'hover:bg-white/5'
                          )}
                        >
                          <Shield className="w-3 h-3" />
                          <span className={tier.color.split(' ')[0]}>{tier.label}</span>
                        </button>
                      ))}
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => updateStatus(member.id, 'suspended')}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                      >
                        <Key className="w-3 h-3" />
                        Suspend access
                      </button>
                      <button
                        onClick={() => updateStatus(member.id, 'terminated')}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <UserX className="w-3 h-3" />
                        Terminate
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal
          onClose={() => setShowAddModal(false)}
          onAdd={(member) => {
            setMembers(prev => [member, ...prev])
            setShowAddModal(false)
          }}
        />
      )}
    </div>
  )
}

function AddMemberModal({
  onClose,
  onAdd
}: {
  onClose: () => void
  onAdd: (member: TeamMember) => void
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: '',
    accessTier: 'member' as AccessTier
  })

  const handleSubmit = () => {
    if (!form.name || !form.email) return
    onAdd({
      id: Date.now().toString(),
      ...form,
      status: 'pending',
      avatar: form.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
      joinedDate: new Date().toISOString().split('T')[0]
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0a0a0f] border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl text-white mb-6">Add team member</h2>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 mb-1 block">Full name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-white/20"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-white/20"
              placeholder="john@lukhas.ai"
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Role</label>
            <input
              type="text"
              value={form.role}
              onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-white/20"
              placeholder="Software Engineer"
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Access tier</label>
            <div className="relative">
              <select
                value={form.accessTier}
                onChange={e => setForm(prev => ({ ...prev, accessTier: e.target.value as AccessTier }))}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-white/20 appearance-none"
              >
                {ACCESS_TIERS.map(tier => (
                  <option key={tier.value} value={tier.value}>{tier.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-lambda-gold text-black rounded-lg font-medium hover:bg-lambda-gold/90 transition-colors"
          >
            Send invite
          </button>
        </div>
      </div>
    </div>
  )
}
